// =============================================================================
// FILE: packages/renderer/src/components/intercom/CorporateIntercom.tsx
// PURPOSE: Corporate Intercom — parent↔child real-time communication hub.
//   • Chat   — text messages, doc-shares, project pushes (WebSocket)
//   • Voice  — WebRTC peer-to-peer audio call
//   • Video  — WebRTC peer-to-peer video call
//   WebRTC signaling is relayed transparently through the existing WebSocket
//   channel (/ws/intercom/{firm_id}/{email}).  STUN-only; no TURN needed for
//   same-org peers behind the same NAT.
// =============================================================================

import {
  useState, useEffect, useRef, useCallback, type JSX,
} from 'react';
import {
  MessageSquare, X, Send, Wifi, WifiOff, Users, FileText,
  FolderKanban, ChevronDown, Minimize2, Phone, Video,
  PhoneOff, MicOff, Mic, VideoOff, PhoneIncoming,
  PhoneMissed, Volume2, UserPlus, CheckCircle2,
} from 'lucide-react';
import { useAuthStore } from '../../stores/auth.store.js';

// ── Types ─────────────────────────────────────────────────────────────────────
interface IntercomMsg {
  id:        string;
  firm_id:   string;
  sender:    string;
  recipient: string | null;
  content:   string;
  msg_type:  'text' | 'doc_share' | 'project_push';
  meta:      Record<string, unknown>;
  ts:        string;
}
interface OnlineNode  { email: string; online: boolean; tier: string; }
type CallState = 'idle' | 'calling' | 'ringing' | 'active' | 'declined' | 'ended';
type CallType  = 'audio' | 'video';
interface IncomingCall {
  from:      string;
  call_type: CallType;
  offer:     RTCSessionDescriptionInit;
}
type Tab = 'chat' | 'call';

// ── ICE servers (public STUN — works in all environments) ────────────────────
const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const TIER_COLOR: Record<string, string> = {
  designer: '#FFD700', contractor: '#38BDF8', technician: '#86EFAC',
};
function tsLabel(iso: string): string {
  try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
}
function buildWsUrl(firmId: string, email: string): string {
  const url   = new URL(window.location.href);
  const proto = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${url.host}/ws/intercom/${encodeURIComponent(firmId)}/${encodeURIComponent(email)}`;
}
function getBackendUrl(): string {
  return (import.meta.env.VITE_BACKEND_URL as string) ?? '';
}
function getInitials(email: string): string {
  const n = email.split('@')[0] ?? '';
  const p = n.replace(/[._-]/g, ' ').split(' ').filter(Boolean);
  return p.length >= 2 ? ((p[0]?.[0] ?? '') + (p[1]?.[0] ?? '')).toUpperCase() : n.slice(0, 2).toUpperCase();
}

// ── Message bubble ────────────────────────────────────────────────────────────
function MsgBubble({ msg, self }: { msg: IntercomMsg; self: boolean }): JSX.Element {
  const isDoc  = msg.msg_type === 'doc_share';
  const isProj = msg.msg_type === 'project_push';
  const accent = isDoc ? '#38BDF8' : isProj ? '#A78BFA' : 'rgb(var(--fg))';
  return (
    <div className={`flex flex-col ${self ? 'items-end' : 'items-start'} mb-3`}>
      {!self && (
        <div className="text-[9px] font-mono mb-1 ml-1"
             style={{ color: TIER_COLOR[msg.sender.split('@')[1] ?? ''] ?? 'rgba(148,172,210,0.5)' }}>
          {msg.sender.split('@')[0]}
        </div>
      )}
      <div className="max-w-[85%] rounded-2xl px-3 py-2 text-[11px] leading-relaxed"
           style={{
             background: self ? 'rgba(56,189,248,0.12)' : 'rgba(12,22,42,0.8)',
             border: `1px solid ${self ? 'rgba(56,189,248,0.25)' : 'rgba(30,50,80,0.5)'}`,
             color: 'rgba(220,232,248,0.9)',
           }}>
        {(isDoc || isProj) && (
          <div className="flex items-center gap-1.5 mb-1 pb-1"
               style={{ borderBottom: `1px solid ${accent}30` }}>
            {isDoc
              ? <FileText    className="h-3 w-3" style={{ color: accent }} />
              : <FolderKanban className="h-3 w-3" style={{ color: accent }} />}
            <span className="text-[9px] uppercase tracking-wider font-bold" style={{ color: accent }}>
              {isDoc ? 'Document Share' : 'Project Push'}
            </span>
          </div>
        )}
        {msg.content}
        {msg.meta?.doc_name !== undefined && (
          <div className="mt-1.5 px-2 py-1 rounded-lg text-[10px] font-mono"
               style={{ background: `${accent}10`, color: accent }}>
            {String(msg.meta.doc_name)}
          </div>
        )}
      </div>
      <div className="text-[9px] mt-0.5 mx-1" style={{ color: 'rgba(148,172,210,0.35)' }}>
        {tsLabel(msg.ts)}
      </div>
    </div>
  );
}

// ── Online node chip ──────────────────────────────────────────────────────────
function NodeChip({
  node, self, onDM, onAudioCall, onVideoCall,
}: {
  node: OnlineNode;
  self: boolean;
  onDM:        (email: string) => void;
  onAudioCall: (email: string) => void;
  onVideoCall: (email: string) => void;
}): JSX.Element {
  return (
    <div className="flex items-center gap-1 px-2 py-1 rounded-full text-[10px]"
         style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.18)' }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#22C55E',
                     flexShrink: 0, boxShadow: '0 0 5px #22C55E' }} />
      <button onClick={() => onDM(node.email)}
              className="font-mono hover:underline" style={{ color: 'rgba(220,232,248,0.8)' }}>
        {node.email.split('@')[0]}
      </button>
      <span className="text-[9px]" style={{ color: TIER_COLOR[node.tier] ?? 'rgba(148,172,210,0.4)' }}>
        {node.tier}
      </span>
      {!self && (
        <>
          <button
            data-testid={`call-audio-${node.email}`}
            title="Voice call"
            onClick={() => onAudioCall(node.email)}
            className="ml-0.5 p-0.5 rounded transition-opacity hover:opacity-100 opacity-60"
            style={{ color: '#4ade80' }}>
            <Phone className="h-3 w-3" />
          </button>
          <button
            data-testid={`call-video-${node.email}`}
            title="Video call"
            onClick={() => onVideoCall(node.email)}
            className="p-0.5 rounded transition-opacity hover:opacity-100 opacity-60"
            style={{ color: '#38BDF8' }}>
            <Video className="h-3 w-3" />
          </button>
        </>
      )}
    </div>
  );
}

// ── Active call panel ─────────────────────────────────────────────────────────
function CallPanel({
  callState, callType, peer, muted, cameraOff,
  localVideoRef, remoteVideoRef,
  onMute, onCamera, onHangup,
}: {
  callState:      CallState;
  callType:       CallType;
  peer:           string;
  muted:          boolean;
  cameraOff:      boolean;
  localVideoRef:  React.RefObject<HTMLVideoElement>;
  remoteVideoRef: React.RefObject<HTMLVideoElement>;
  onMute:    () => void;
  onCamera:  () => void;
  onHangup:  () => void;
}): JSX.Element {
  const statusLabel = {
    calling:  `Calling ${peer.split('@')[0]}…`,
    ringing:  `Incoming from ${peer.split('@')[0]}`,
    active:   `Connected · ${peer.split('@')[0]}`,
    declined: 'Call declined',
    ended:    'Call ended',
    idle:     '',
  }[callState] ?? '';

  return (
    <div className="flex flex-col flex-1 overflow-hidden"
         style={{ background: 'rgba(4,8,18,0.95)' }}>
      {/* Remote video / audio placeholder */}
      <div className="relative flex-1 flex items-center justify-center">
        {callType === 'video' && callState === 'active' ? (
          <video
            ref={remoteVideoRef}
            autoPlay playsInline
            data-testid="remote-video"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div className="flex flex-col items-center gap-3">
            {/* Avatar circle */}
            <div style={{
              width: 72, height: 72, borderRadius: '50%',
              background: 'rgba(37,181,168,0.12)',
              border: `2px solid ${callState === 'active' ? '#25B5A8' : 'rgba(37,181,168,0.3)'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 24, fontWeight: 800, color: '#5eead4',
              boxShadow: callState === 'active' ? '0 0 24px rgba(37,181,168,0.35)' : 'none',
              animation: callState === 'active' ? 'pulse 2s infinite' : 'none',
            }}>
              {getInitials(peer)}
            </div>
            <div style={{ fontSize: 13, color: 'rgba(220,232,248,0.85)', fontWeight: 600 }}>
              {peer.split('@')[0]}
            </div>
            <div style={{ fontSize: 11, color: 'rgba(148,172,210,0.5)' }}>{statusLabel}</div>
            {callState === 'active' && (
              <div className="flex items-center gap-1.5"
                   style={{ fontSize: 10, color: '#4ade80' }}>
                <Volume2 className="h-3 w-3" />
                <span>Audio connected</span>
              </div>
            )}
          </div>
        )}

        {/* Local video (PiP) */}
        {callType === 'video' && !cameraOff && (
          <video
            ref={localVideoRef}
            autoPlay playsInline muted
            data-testid="local-video"
            style={{
              position: 'absolute', bottom: 8, right: 8,
              width: 90, height: 68, borderRadius: 8,
              objectFit: 'cover', border: '1.5px solid rgba(37,181,168,0.4)',
              background: '#000',
            }}
          />
        )}
      </div>

      {/* Call controls */}
      <div className="flex items-center justify-center gap-3 py-4 shrink-0"
           style={{ borderTop: '1px solid rgba(20,35,60,0.6)' }}>
        <button
          data-testid="call-mute-btn"
          onClick={onMute}
          title={muted ? 'Unmute' : 'Mute'}
          className="flex items-center justify-center w-10 h-10 rounded-full transition-all"
          style={{
            background: muted ? 'rgba(239,68,68,0.15)' : 'rgba(30,50,80,0.5)',
            border: `1px solid ${muted ? 'rgba(239,68,68,0.4)' : 'rgba(30,50,80,0.6)'}`,
            color: muted ? '#EF4444' : 'rgba(148,172,210,0.7)',
          }}>
          {muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </button>

        {callType === 'video' && (
          <button
            data-testid="call-camera-btn"
            onClick={onCamera}
            title={cameraOff ? 'Turn camera on' : 'Turn camera off'}
            className="flex items-center justify-center w-10 h-10 rounded-full transition-all"
            style={{
              background: cameraOff ? 'rgba(239,68,68,0.15)' : 'rgba(30,50,80,0.5)',
              border: `1px solid ${cameraOff ? 'rgba(239,68,68,0.4)' : 'rgba(30,50,80,0.6)'}`,
              color: cameraOff ? '#EF4444' : 'rgba(148,172,210,0.7)',
            }}>
            {cameraOff ? <VideoOff className="h-4 w-4" /> : <Video className="h-4 w-4" />}
          </button>
        )}

        <button
          data-testid="call-hangup-btn"
          onClick={onHangup}
          title="Hang up"
          className="flex items-center justify-center w-12 h-12 rounded-full transition-all"
          style={{
            background: 'rgba(239,68,68,0.2)',
            border: '1px solid rgba(239,68,68,0.45)',
            color: '#EF4444',
          }}>
          <PhoneOff className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function CorporateIntercom(): JSX.Element {
  const user   = useAuthStore((s) => s.user);
  const email  = user?.email ?? '';
  const firmId = email.split('@')[1] ?? 'default';

  // ── Chat state ──────────────────────────────────────────────────────────────
  const [open,      setOpen]      = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [tab,       setTab]       = useState<Tab>('chat');
  const [messages,  setMessages]  = useState<IntercomMsg[]>([]);
  const [nodes,     setNodes]     = useState<OnlineNode[]>([]);
  const [draft,     setDraft]     = useState('');
  const [dmTarget,  setDmTarget]  = useState<string | null>(null);
  const [wsStatus,  setWsStatus]  = useState<'connecting' | 'open' | 'closed'>('closed');
  const [unread,    setUnread]    = useState(0);

  // ── Call state ──────────────────────────────────────────────────────────────
  const [callState,    setCallState]    = useState<CallState>('idle');
  const [callType,     setCallType]     = useState<CallType>('audio');
  const [callPeer,     setCallPeer]     = useState('');
  const [incomingCall, setIncomingCall] = useState<IncomingCall | null>(null);
  const [muted,        setMuted]        = useState(false);
  const [cameraOff,    setCameraOff]    = useState(false);

  // ── Seat invite state ────────────────────────────────────────────────────────
  const [showInvite,   setShowInvite]   = useState(false);
  const [inviteEmail,  setInviteEmail]  = useState('');
  const [inviteName,   setInviteName]   = useState('');
  const [inviteStatus, setInviteStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [inviteMsg,    setInviteMsg]    = useState('');

  // ── Refs ────────────────────────────────────────────────────────────────────
  const wsRef          = useRef<WebSocket | null>(null);
  const msgEndRef      = useRef<HTMLDivElement>(null);
  const pingTimer      = useRef<ReturnType<typeof setInterval> | null>(null);
  const pcRef          = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const pendingIceRef  = useRef<RTCIceCandidateInit[]>([]);
  const localVideoRef  = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const pendingOfferRef = useRef<IncomingCall | null>(null);

  // ── Scroll to bottom ────────────────────────────────────────────────────────
  useEffect(() => {
    if (open && !minimized && tab === 'chat') {
      msgEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open, minimized, tab]);

  // ── History + nodes ─────────────────────────────────────────────────────────
  const loadHistory = useCallback(async () => {
    try {
      const r = await fetch(`${getBackendUrl()}/api/intercom/messages/${firmId}`);
      if (r.ok) setMessages(await r.json() as IntercomMsg[]);
    } catch { /* offline */ }
  }, [firmId]);

  const loadNodes = useCallback(async () => {
    try {
      const r = await fetch(`${getBackendUrl()}/api/intercom/nodes/${firmId}`);
      if (r.ok) setNodes(await r.json() as OnlineNode[]);
    } catch { /* ignore */ }
  }, [firmId]);

  // ── WebRTC helpers ──────────────────────────────────────────────────────────
  const closePeer = useCallback(() => {
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    pcRef.current?.close();
    pcRef.current = null;
    pendingIceRef.current = [];
    if (localVideoRef.current)  localVideoRef.current.srcObject  = null;
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
    setMuted(false);
    setCameraOff(false);
  }, []);

  const hangupCall = useCallback(() => {
    if (callPeer && wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'call_hangup', to: callPeer }));
    }
    closePeer();
    setCallState('ended');
    setTimeout(() => { setCallState('idle'); setCallPeer(''); }, 1800);
  }, [callPeer, closePeer]);

  // ── Issue child-license seat ─────────────────────────────────────────────────
  const issueChildSeat = useCallback(async () => {
    const targetEmail = inviteEmail.trim().toLowerCase();
    if (!targetEmail || !targetEmail.includes('@')) return;
    setInviteStatus('sending');
    try {
      const r = await fetch(`${getBackendUrl()}/api/licenses/child`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          child_email: targetEmail,
          child_name:  inviteName.trim() || undefined,
          parent_domain: firmId,
        }),
      });
      const data = await r.json() as { ok?: boolean; already?: boolean; detail?: string };
      if (!r.ok) throw new Error(data.detail ?? 'Failed to issue seat');
      setInviteStatus('done');
      setInviteMsg(data.already ? 'Seat already exists for that email.' : 'Seat issued! They can now connect to the intercom.');
      setInviteEmail('');
      setInviteName('');
      // Notify HolographicEngineCore to refresh immediately
      window.dispatchEvent(new CustomEvent('r2:child-license-issued'));
      setTimeout(() => { setShowInvite(false); setInviteStatus('idle'); setInviteMsg(''); }, 3500);
    } catch (err: unknown) {
      setInviteStatus('error');
      setInviteMsg(err instanceof Error ? err.message : 'Unknown error');
    }
  }, [inviteEmail, inviteName, firmId]);

  const buildPeerConnection = useCallback((peer: string): RTCPeerConnection => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    pcRef.current = pc;

    pc.onicecandidate = (e) => {
      if (e.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'ice_candidate', to: peer, candidate: e.candidate.toJSON(),
        }));
      }
    };

    pc.ontrack = (e) => {
      if (remoteVideoRef.current && e.streams[0]) {
        remoteVideoRef.current.srcObject = e.streams[0];
      }
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') setCallState('active');
      if (['disconnected', 'failed', 'closed'].includes(pc.connectionState)) {
        closePeer();
        setCallState('ended');
        setTimeout(() => { setCallState('idle'); setCallPeer(''); }, 1800);
      }
    };
    return pc;
  }, [closePeer]);

  const startCall = useCallback(async (peer: string, type: CallType) => {
    if (callState !== 'idle') return;
    setCallPeer(peer);
    setCallType(type);
    setCallState('calling');
    setTab('call');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: type === 'video',
      });
      localStreamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;

      const pc = buildPeerConnection(peer);
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      wsRef.current?.send(JSON.stringify({
        type: 'call_offer', to: peer, call_type: type, offer: pc.localDescription,
      }));
    } catch (err) {
      console.error('startCall failed:', err);
      setCallState('idle');
      setCallPeer('');
    }
  }, [callState, buildPeerConnection]);

  const answerCall = useCallback(async () => {
    const ic = pendingOfferRef.current;
    if (!ic) return;
    setIncomingCall(null);
    setCallPeer(ic.from);
    setCallType(ic.call_type);
    setCallState('active');
    setTab('call');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: ic.call_type === 'video',
      });
      localStreamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;

      const pc = buildPeerConnection(ic.from);
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));

      await pc.setRemoteDescription(new RTCSessionDescription(ic.offer));

      // Flush queued ICE candidates
      for (const c of pendingIceRef.current) {
        try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch { /* ignore */ }
      }
      pendingIceRef.current = [];

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      wsRef.current?.send(JSON.stringify({
        type: 'call_answer', to: ic.from, answer: pc.localDescription,
      }));
    } catch (err) {
      console.error('answerCall failed:', err);
      setCallState('idle');
      setCallPeer('');
    }
  }, [buildPeerConnection]);

  const declineCall = useCallback(() => {
    const ic = pendingOfferRef.current;
    if (ic) {
      wsRef.current?.send(JSON.stringify({ type: 'call_hangup', to: ic.from }));
    }
    pendingOfferRef.current = null;
    setIncomingCall(null);
  }, []);

  const toggleMute = useCallback(() => {
    localStreamRef.current?.getAudioTracks().forEach((t) => { t.enabled = muted; });
    setMuted((m) => !m);
  }, [muted]);

  const toggleCamera = useCallback(() => {
    localStreamRef.current?.getVideoTracks().forEach((t) => { t.enabled = cameraOff; });
    setCameraOff((c) => !c);
  }, [cameraOff]);

  // ── WebSocket ───────────────────────────────────────────────────────────────
  const connectWs = useCallback(() => {
    if (!email || wsRef.current?.readyState === WebSocket.OPEN) return;
    setWsStatus('connecting');
    const ws = new WebSocket(buildWsUrl(firmId, email));
    wsRef.current = ws;

    ws.onopen = () => {
      setWsStatus('open');
      pingTimer.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      }, 25_000);
    };

    ws.onmessage = (e) => {
      let evt: Record<string, unknown>;
      try { evt = JSON.parse(e.data as string) as Record<string, unknown>; }
      catch { return; }

      const evtType = String(evt.event ?? evt.type ?? '');

      // ── Chat events ─────────────────────────────────────────────────────
      if (evtType === 'new_message') {
        const msg = evt as unknown as IntercomMsg;
        setMessages((prev) => [...prev, msg]);
        if (!open || minimized || tab !== 'chat') setUnread((n) => n + 1);
        return;
      }
      if (evtType === 'member_joined' || evtType === 'member_left') {
        void loadNodes();
        return;
      }

      // ── WebRTC signaling events ─────────────────────────────────────────
      if (evtType === 'call_offer') {
        const offer: IncomingCall = {
          from:      String(evt.from ?? ''),
          call_type: (evt.call_type === 'video' ? 'video' : 'audio') as CallType,
          offer:     evt.offer as RTCSessionDescriptionInit,
        };
        pendingOfferRef.current = offer;
        setIncomingCall(offer);
        return;
      }

      if (evtType === 'call_answer' && pcRef.current) {
        const sd = evt.answer as RTCSessionDescriptionInit;
        pcRef.current.setRemoteDescription(new RTCSessionDescription(sd))
          .then(() => {
            setCallState('active');
            for (const c of pendingIceRef.current) {
              void pcRef.current?.addIceCandidate(new RTCIceCandidate(c));
            }
            pendingIceRef.current = [];
          })
          .catch(console.error);
        return;
      }

      if (evtType === 'ice_candidate') {
        const c = evt.candidate as RTCIceCandidateInit;
        if (pcRef.current?.remoteDescription) {
          void pcRef.current.addIceCandidate(new RTCIceCandidate(c));
        } else {
          pendingIceRef.current.push(c);
        }
        return;
      }

      if (evtType === 'call_hangup') {
        closePeer();
        setIncomingCall(null);
        pendingOfferRef.current = null;
        setCallState('ended');
        setTimeout(() => { setCallState('idle'); setCallPeer(''); }, 1800);
        return;
      }
    };

    ws.onclose = () => {
      setWsStatus('closed');
      if (pingTimer.current) clearInterval(pingTimer.current);
      setTimeout(() => { if (open) connectWs(); }, 4_000);
    };
    ws.onerror = () => { ws.close(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, firmId, open, minimized, tab, closePeer, loadNodes]);

  useEffect(() => {
    if (open) {
      setUnread(0);
      void loadHistory();
      void loadNodes();
      connectWs();
    } else {
      wsRef.current?.close();
      if (pingTimer.current) clearInterval(pingTimer.current);
    }
    return () => {
      wsRef.current?.close();
      if (pingTimer.current) clearInterval(pingTimer.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Send chat message ───────────────────────────────────────────────────────
  const sendMessage = async () => {
    const text = draft.trim();
    if (!text || !email) return;
    setDraft('');
    try {
      await fetch(`${getBackendUrl()}/api/intercom/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firm_id: firmId, sender: email,
          recipient: dmTarget, content: text, msg_type: 'text',
        }),
      });
    } catch { /* optimistic UI already shown */ }
  };

  if (!email) return <></>;

  const peers = nodes.filter((n) => n.email !== email);
  const inCall = ['calling', 'ringing', 'active'].includes(callState);

  return (
    <>
      {/* ── Floating toggle ─────────────────────────────────────────────── */}
      <button
        data-testid="intercom-toggle"
        onClick={() => { setOpen(!open); setMinimized(false); }}
        className="fixed bottom-32 right-5 z-[9600] flex items-center justify-center w-11 h-11 rounded-full shadow-xl transition-all"
        style={{
          background: 'rgba(30,35,50,0.92)',
          border: inCall
            ? '1.5px solid rgba(74,222,128,0.6)'
            : wsStatus === 'open' ? '1.5px solid rgba(34,197,94,0.45)' : '1.5px solid rgba(30,50,80,0.5)',
          backdropFilter: 'blur(12px)',
        }}
        title="Corporate Intercom"
      >
        {inCall
          ? <Phone className="h-5 w-5 text-green-400" style={{ animation: 'pulse 1s infinite' }} strokeWidth={1.5} />
          : <MessageSquare className="h-5 w-5" style={{ color: 'rgba(220,232,248,0.8)' }} strokeWidth={1.5} />}
        {(unread > 0 || incomingCall) && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full flex items-center justify-center text-[9px] font-bold"
                style={{ background: incomingCall ? '#4ade80' : '#38BDF8', color: '#0b0d10' }}>
            {incomingCall ? '!' : (unread > 9 ? '9+' : unread)}
          </span>
        )}
      </button>

      {/* ── Incoming call banner (always visible even if panel closed) ──── */}
      {incomingCall && !open && (
        <div
          data-testid="incoming-call-banner"
          className="fixed bottom-44 right-5 z-[9600] flex items-center gap-3 px-4 py-3 rounded-2xl shadow-2xl"
          style={{
            background: 'rgba(10,18,35,0.97)',
            border: '1px solid rgba(74,222,128,0.45)',
            backdropFilter: 'blur(16px)',
            width: 280,
            animation: 'slideIn 0.3s ease',
          }}
        >
          <div className="flex items-center justify-center w-10 h-10 rounded-full shrink-0"
               style={{ background: 'rgba(74,222,128,0.12)', border: '1px solid rgba(74,222,128,0.3)' }}>
            {incomingCall.call_type === 'video'
              ? <Video  className="h-4 w-4 text-green-400" />
              : <Phone  className="h-4 w-4 text-green-400" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-semibold" style={{ color: 'rgba(220,232,248,0.95)' }}>
              Incoming {incomingCall.call_type} call
            </div>
            <div className="text-[10px] font-mono truncate" style={{ color: '#4ade80' }}>
              {incomingCall.from}
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              data-testid="answer-call-btn"
              onClick={() => { void answerCall(); setOpen(true); }}
              className="flex items-center justify-center w-8 h-8 rounded-full transition"
              style={{ background: 'rgba(74,222,128,0.18)', border: '1px solid rgba(74,222,128,0.4)', color: '#4ade80' }}>
              <Phone className="h-3.5 w-3.5" />
            </button>
            <button
              data-testid="decline-call-btn"
              onClick={declineCall}
              className="flex items-center justify-center w-8 h-8 rounded-full transition"
              style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.35)', color: '#EF4444' }}>
              <PhoneMissed className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* ── Main panel ──────────────────────────────────────────────────── */}
      {open && (
        <div
          data-testid="intercom-panel"
          className="fixed bottom-44 right-5 z-[9600] flex flex-col rounded-2xl shadow-2xl overflow-hidden"
          style={{
            width: 360,
            height: minimized ? 52 : 520,
            transition: 'height 0.25s cubic-bezier(0.4,0,0.2,1)',
            background: 'rgba(8,14,26,0.97)',
            border: '1px solid rgba(30,50,80,0.55)',
            backdropFilter: 'blur(20px)',
          }}
        >
          {/* ── Header ──────────────────────────────────────────────────── */}
          <div className="flex items-center gap-2 px-4 py-3 shrink-0"
               style={{ borderBottom: minimized ? 'none' : '1px solid rgba(20,35,60,0.6)' }}>
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              {wsStatus === 'open'
                ? <Wifi    className="h-3.5 w-3.5 text-green-400" strokeWidth={1.75} />
                : <WifiOff className="h-3.5 w-3.5 text-zinc-500"  strokeWidth={1.75} />}
              <span className="text-[12px] font-semibold truncate" style={{ color: 'rgba(220,232,248,0.95)' }}>
                Corporate Intercom
              </span>
              <span className="text-[9px] px-1.5 py-0.5 rounded font-mono shrink-0"
                    style={{ background: 'rgba(56,189,248,0.10)', color: '#38BDF8', border: '1px solid rgba(56,189,248,0.22)' }}>
                {firmId}
              </span>
            </div>
            <button onClick={() => setMinimized(!minimized)}
                    className="p-1 rounded opacity-50 hover:opacity-100 transition-opacity">
              {minimized
                ? <ChevronDown className="h-3.5 w-3.5" style={{ color: 'rgba(220,232,248,0.8)' }} />
                : <Minimize2   className="h-3.5 w-3.5" style={{ color: 'rgba(220,232,248,0.8)' }} />}
            </button>
            <button onClick={() => { setOpen(false); if (inCall) hangupCall(); }}
                    className="p-1 rounded opacity-50 hover:opacity-100 transition-opacity">
              <X className="h-3.5 w-3.5" style={{ color: 'rgba(220,232,248,0.8)' }} />
            </button>
          </div>

          {!minimized && (
            <>
              {/* ── Tab bar ─────────────────────────────────────────────── */}
              <div className="flex shrink-0"
                   style={{ borderBottom: '1px solid rgba(20,35,60,0.5)' }}>
                {(['chat', 'call'] as Tab[]).map((t) => (
                  <button
                    key={t}
                    data-testid={`intercom-tab-${t}`}
                    onClick={() => setTab(t)}
                    className="flex-1 py-2 text-[11px] font-semibold flex items-center justify-center gap-1.5 transition-all"
                    style={{
                      color: tab === t ? '#38BDF8' : 'rgba(148,172,210,0.45)',
                      borderBottom: tab === t ? '2px solid #38BDF8' : '2px solid transparent',
                      background: 'none', cursor: 'pointer',
                    } as React.CSSProperties}
                  >
                    {t === 'chat'
                      ? <><MessageSquare className="h-3 w-3" /> Chat</>
                      : <><Phone className="h-3 w-3" /> Calls {inCall && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80', animation: 'pulse 1s infinite', display: 'inline-block' }} />}</>}
                  </button>
                ))}
              </div>

              {/* ── Incoming call overlay (inside panel) ────────────────── */}
              {incomingCall && tab === 'call' && (
                <div className="flex flex-col items-center justify-center gap-4 flex-1"
                     data-testid="incoming-call-overlay"
                     style={{ background: 'rgba(4,10,20,0.95)' }}>
                  <div style={{
                    width: 80, height: 80, borderRadius: '50%',
                    background: 'rgba(74,222,128,0.1)',
                    border: '2px solid rgba(74,222,128,0.4)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 28, fontWeight: 800, color: '#4ade80',
                    animation: 'pulse 1.5s infinite',
                  }}>
                    {getInitials(incomingCall.from)}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'rgba(220,232,248,0.9)', textAlign: 'center' }}>
                      {incomingCall.from.split('@')[0]}
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(148,172,210,0.5)', textAlign: 'center', marginTop: 4 }}>
                      Incoming {incomingCall.call_type === 'video' ? 'video' : 'voice'} call
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <button
                      data-testid="answer-call-btn"
                      onClick={() => void answerCall()}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-full text-[12px] font-bold"
                      style={{ background: 'rgba(74,222,128,0.18)', border: '1px solid rgba(74,222,128,0.45)', color: '#4ade80' }}>
                      <Phone className="h-4 w-4" /> Answer
                    </button>
                    <button
                      data-testid="decline-call-btn"
                      onClick={declineCall}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-full text-[12px] font-bold"
                      style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.35)', color: '#EF4444' }}>
                      <PhoneOff className="h-4 w-4" /> Decline
                    </button>
                  </div>
                </div>
              )}

              {/* ── Active/ringing/calling call panel ───────────────────── */}
              {!incomingCall && tab === 'call' && inCall && (
                <CallPanel
                  callState={callState} callType={callType} peer={callPeer}
                  muted={muted} cameraOff={cameraOff}
                  localVideoRef={localVideoRef} remoteVideoRef={remoteVideoRef}
                  onMute={toggleMute} onCamera={toggleCamera} onHangup={hangupCall}
                />
              )}

              {/* ── Idle call tab — show online peers with call buttons ─── */}
              {!incomingCall && tab === 'call' && !inCall && (
                <div className="flex-1 overflow-y-auto px-4 py-4">
                  {callState === 'ended' && (
                    <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg text-[11px]"
                         style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#F87171' }}>
                      <PhoneMissed className="h-3.5 w-3.5" /> Call ended
                    </div>
                  )}
                  <div className="text-[10px] font-bold uppercase tracking-widest mb-3"
                       style={{ color: 'rgba(148,172,210,0.35)' }}>
                    Online peers — click to call
                  </div>
                  {peers.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 py-6 text-center">
                      <Users className="h-8 w-8" style={{ color: 'rgba(148,172,210,0.15)' }} />
                      <p className="text-[11px]" style={{ color: 'rgba(148,172,210,0.35)' }}>
                        No peers online right now
                      </p>
                      <p className="text-[10px]" style={{ color: 'rgba(148,172,210,0.2)' }}>
                        Child license holders appear here when connected
                      </p>

                      {/* Invite flow */}
                      {!showInvite ? (
                        <button
                          data-testid="invite-peer-btn"
                          onClick={() => setShowInvite(true)}
                          className="mt-2 flex items-center gap-1.5 px-4 py-2 rounded-xl text-[11px] font-semibold transition-all"
                          style={{
                            background: 'rgba(45,181,168,0.1)', border: '1px solid rgba(45,181,168,0.3)',
                            color: '#5eead4',
                          }}
                        >
                          <UserPlus className="h-3.5 w-3.5" /> Invite a Crew Member
                        </button>
                      ) : (
                        <div
                          data-testid="invite-form"
                          className="w-full mt-2 px-3"
                          style={{ textAlign: 'left' }}
                        >
                          <div className="text-[10px] font-bold uppercase tracking-widest mb-2"
                               style={{ color: 'rgba(45,181,168,0.6)' }}>
                            Issue Child Seat
                          </div>
                          <input
                            data-testid="invite-email-input"
                            type="email"
                            placeholder="colleague@company.com"
                            value={inviteEmail}
                            onChange={e => setInviteEmail(e.target.value)}
                            className="w-full h-8 px-3 mb-2 rounded-lg text-[11px] focus:outline-none"
                            style={{ background: 'rgba(6,12,26,0.7)', border: '1px solid rgba(45,181,168,0.3)', color: 'rgba(220,232,248,0.9)' }}
                          />
                          <input
                            data-testid="invite-name-input"
                            type="text"
                            placeholder="Name (optional)"
                            value={inviteName}
                            onChange={e => setInviteName(e.target.value)}
                            className="w-full h-8 px-3 mb-3 rounded-lg text-[11px] focus:outline-none"
                            style={{ background: 'rgba(6,12,26,0.7)', border: '1px solid rgba(30,50,80,0.5)', color: 'rgba(220,232,248,0.9)' }}
                          />
                          {inviteMsg && (
                            <div className="mb-2 text-[10px] flex items-center gap-1.5 px-2 py-1.5 rounded-lg"
                                 style={{
                                   background: inviteStatus === 'error' ? 'rgba(239,68,68,0.08)' : 'rgba(74,222,128,0.08)',
                                   border: `1px solid ${inviteStatus === 'error' ? 'rgba(239,68,68,0.3)' : 'rgba(74,222,128,0.3)'}`,
                                   color: inviteStatus === 'error' ? '#F87171' : '#4ade80',
                                 }}>
                              {inviteStatus === 'done' && <CheckCircle2 className="h-3 w-3" />}
                              {inviteMsg}
                            </div>
                          )}
                          <div className="flex gap-2">
                            <button
                              data-testid="invite-submit-btn"
                              onClick={() => void issueChildSeat()}
                              disabled={!inviteEmail.includes('@') || inviteStatus === 'sending'}
                              className="flex-1 h-8 rounded-lg text-[11px] font-semibold transition disabled:opacity-40"
                              style={{ background: 'rgba(45,181,168,0.18)', border: '1px solid rgba(45,181,168,0.4)', color: '#5eead4' }}
                            >
                              {inviteStatus === 'sending' ? 'Sending…' : 'Issue Seat'}
                            </button>
                            <button
                              data-testid="invite-cancel-btn"
                              onClick={() => { setShowInvite(false); setInviteEmail(''); setInviteName(''); setInviteStatus('idle'); setInviteMsg(''); }}
                              className="h-8 px-3 rounded-lg text-[11px] transition"
                              style={{ background: 'rgba(30,50,80,0.3)', border: '1px solid rgba(30,50,80,0.5)', color: 'rgba(148,172,210,0.6)' }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    peers.map((n) => (
                      <div key={n.email}
                           className="flex items-center gap-3 px-3 py-3 rounded-xl mb-2"
                           style={{ background: 'rgba(10,20,40,0.6)', border: '1px solid rgba(20,35,60,0.5)' }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                          background: 'rgba(37,181,168,0.1)', border: '1px solid rgba(37,181,168,0.25)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 12, fontWeight: 700, color: '#5eead4',
                        }}>
                          {getInitials(n.email)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[12px] font-semibold truncate"
                               style={{ color: 'rgba(220,232,248,0.9)' }}>
                            {n.email.split('@')[0]}
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#4ade80' }} />
                            <span className="text-[9px]" style={{ color: TIER_COLOR[n.tier] ?? 'rgba(148,172,210,0.4)' }}>
                              {n.tier} · online
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <button
                            data-testid={`call-audio-${n.email}`}
                            onClick={() => void startCall(n.email, 'audio')}
                            className="flex items-center justify-center w-8 h-8 rounded-full transition"
                            style={{ background: 'rgba(74,222,128,0.12)', border: '1px solid rgba(74,222,128,0.3)', color: '#4ade80' }}
                            title="Voice call">
                            <Phone className="h-3.5 w-3.5" />
                          </button>
                          <button
                            data-testid={`call-video-${n.email}`}
                            onClick={() => void startCall(n.email, 'video')}
                            className="flex items-center justify-center w-8 h-8 rounded-full transition"
                            style={{ background: 'rgba(56,189,248,0.12)', border: '1px solid rgba(56,189,248,0.3)', color: '#38BDF8' }}
                            title="Video call">
                            <Video className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* ── Chat tab ────────────────────────────────────────────── */}
              {tab === 'chat' && (
                <>
                  {/* Online nodes strip */}
                  {peers.length > 0 && (
                    <div className="px-3 py-2 flex items-center gap-2 flex-wrap shrink-0"
                         style={{ borderBottom: '1px solid rgba(20,35,60,0.4)' }}>
                      <Users className="h-3 w-3 shrink-0" style={{ color: 'rgba(148,172,210,0.35)' }} strokeWidth={1.75} />
                      {peers.map((n) => (
                        <NodeChip
                          key={n.email} node={n} self={n.email === email}
                          onDM={(em) => setDmTarget(em === dmTarget ? null : em)}
                          onAudioCall={(em) => void startCall(em, 'audio')}
                          onVideoCall={(em) => void startCall(em, 'video')}
                        />
                      ))}
                    </div>
                  )}

                  {/* DM target strip */}
                  {dmTarget && (
                    <div className="px-3 py-1.5 flex items-center gap-2 shrink-0"
                         style={{ background: 'rgba(56,189,248,0.05)', borderBottom: '1px solid rgba(56,189,248,0.12)' }}>
                      <span className="text-[10px]" style={{ color: 'rgba(148,172,210,0.5)' }}>DM →</span>
                      <span className="text-[10px] font-mono" style={{ color: '#38BDF8' }}>{dmTarget}</span>
                      <button onClick={() => setDmTarget(null)} className="ml-auto opacity-50 hover:opacity-100">
                        <X className="h-3 w-3" style={{ color: '#38BDF8' }} />
                      </button>
                    </div>
                  )}

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto px-3 py-3"
                       data-testid="intercom-messages">
                    {messages.length === 0 && (
                      <div className="h-full flex flex-col items-center justify-center text-center px-6">
                        <MessageSquare className="h-8 w-8 mb-3" style={{ color: 'rgba(148,172,210,0.14)' }} strokeWidth={1} />
                        <p className="text-[11px]" style={{ color: 'rgba(148,172,210,0.35)' }}>
                          No messages yet. Say hello to your team.
                        </p>
                      </div>
                    )}
                    {messages.map((m) => (
                      <MsgBubble key={m.id} msg={m} self={m.sender === email} />
                    ))}
                    <div ref={msgEndRef} />
                  </div>

                  {/* Input */}
                  <div className="px-3 py-3 flex items-center gap-2 shrink-0"
                       style={{ borderTop: '1px solid rgba(20,35,60,0.5)' }}>
                    <input
                      data-testid="intercom-input"
                      type="text"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void sendMessage(); } }}
                      placeholder={dmTarget ? `Message ${dmTarget.split('@')[0]}…` : 'Message your team…'}
                      className="flex-1 h-9 px-3 rounded-xl text-[11px] focus:outline-none transition"
                      style={{ background: 'rgba(6,12,26,0.7)', border: '1px solid rgba(30,50,80,0.5)', color: 'rgba(220,232,248,0.9)' }}
                    />
                    <button
                      data-testid="intercom-send"
                      onClick={() => void sendMessage()}
                      disabled={!draft.trim()}
                      className="flex items-center justify-center w-9 h-9 rounded-xl transition-all disabled:opacity-40"
                      style={{ background: 'rgba(56,189,248,0.14)', border: '1px solid rgba(56,189,248,0.35)', color: '#38BDF8' }}>
                      <Send className="h-4 w-4" strokeWidth={1.75} />
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      <style>{`
        @keyframes slideIn { from { transform: translateX(20px); opacity: 0; } to { transform: none; opacity: 1; } }
        @keyframes pulse   { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }
      `}</style>
    </>
  );
}
